import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { PROGRAMMES } from "@/data/content";

const APPROACH = [
  {
    n: "01",
    title: "Diagnose",
    body: "We identify where the leadership transition is breaking down: visibility, influence, positioning, retention, or succession readiness.",
  },
  {
    n: "02",
    title: "Design",
    body: "We build targeted interventions around the actual organisational gap, not off-the-shelf leadership content.",
  },
  {
    n: "03",
    title: "Deliver",
    body: "We work directly with leaders in scope, with sponsor visibility throughout. The behavioural and leadership shift becomes visible within months.",
  },
];

const FAQ = [
  {
    q: "How is this different from traditional leadership development programmes?",
    a: "This is not a leadership development programme. It is targeted, strategic work with the women your business is already relying on, to ensure they can operate at the level the role requires. The focus is influence, visibility, positioning, and stakeholder navigation, not generic leadership content.",
  },
  {
    q: "What measurable outcomes do organisations typically see?",
    a: "Stronger promotion conversion at senior level, improved retention of high-potential women, more visible leadership presence in business-critical conversations, and a measurably broader contribution from leaders who were previously trapped in execution.",
  },
  {
    q: "How quickly does leadership behaviour start to shift?",
    a: "Visible behavioural change is typically observed within three to six months, both by sponsors and by the leaders themselves. Strategic positioning, stakeholder influence, and decision-making presence tend to shift the fastest.",
  },
  {
    q: "Can this work across multiple countries or regions?",
    a: "Yes. We work in person in London, hybrid across the UK and Europe, and remotely for individual coaching. The Atlantis simulation is delivered on-site at your offices or a venue of your choosing, anywhere in Europe.",
  },
  {
    q: "Who tends to benefit most from these interventions?",
    a: "Organisations with strong female talent that is not yet translating into senior leadership, where capability is established but influence, visibility, and strategic positioning are the limiting factors. The work assumes seriousness from the sponsor and willingness to look at how progression actually plays out internally.",
  },
];

const OFFERS = [
  {
    slug: PROGRAMMES[0].slug,
    eyebrow: "Cohort Programme",
    title: PROGRAMMES[0].title,
    body: "A targeted programme for senior women who are already delivering at a high level but are not yet operating with sufficient influence, visibility, or strategic authority. Focused on leadership presence, stakeholder management, positioning, and senior decision-making.",
    format: "8–10 participants · Multi-week · London or hybrid",
  },
  {
    slug: PROGRAMMES[1].slug,
    eyebrow: "Executive Coaching",
    title: PROGRAMMES[1].title,
    body: "Strategic one-to-one coaching for senior women operating at or approaching executive level. Focused on leadership identity, influence, visibility, organisational navigation, and progression into broader authority.",
    format: "6 × 60-minute sessions per quarter · 1 stakeholder check-in · Minimum 3 coachees per organisation",
  },
  {
    slug: PROGRAMMES[2].slug,
    eyebrow: "Strategic Leadership Simulation",
    title: PROGRAMMES[2].title,
    body: "A high-stakes leadership simulation in which participants govern the High Council of an advanced civilisation facing escalating crises, hidden agendas, and decisions with compounding consequences. Designed to reveal real leadership behaviour and strengthen judgement, influence, and alignment when certainty disappears.",
    format: "15–30 participants · Full day · Facilitated debrief",
  },
];

const PERSONAS = [
  { t: "HR & L&D Directors",         b: "Need leadership interventions that stand up to executive scrutiny and deliver measurable progression." },
  { t: "Heads of Talent",            b: "Responsible for leadership pipelines that stall at Director and VP level despite strong technical performance." },
  { t: "Business Leaders",           b: "Need high-performing women to step into broader influence, not remain trapped in execution." },
  { t: "DEI & Inclusion Leaders",    b: "Accountable for progression, retention, and leadership representation, not just initiatives and intent." },
];

const ENGAGEMENT = [
  "Confidential discovery with sponsors, HR, and business stakeholders.",
  "Tailored participant scope, objectives, and measurable success criteria.",
  "Sponsor-aligned leadership outcomes from beginning to end.",
  "Structured visibility and progress check-ins throughout the engagement.",
  "Final review focused on behavioural and business outcomes, not satisfaction scores.",
];

export default function Corporate() {
  return (
    <div data-testid="corporate-page">
      {/* Hero */}
      <section className="pt-36 lg:pt-44 pb-20 bg-gwa-bg">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />Corporate Solutions</p>
            </Reveal>
          </div>
          <div className="lg:col-span-10">
            <Reveal delay={120}>
              <h1 className="gwa-display text-5xl lg:text-7xl text-gwa-primary leading-[1.02]">
                Your leadership pipeline is not translating into <em className="italic font-light text-gwa-purple">influential leaders.</em>
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 space-y-5 text-lg text-gwa-muted-fg max-w-3xl leading-relaxed">
                <p>Your high-performing women are delivering.</p>
                <p>But too few are transitioning into visible, influential senior leaders.</p>
                <p>The issue is rarely capability. It is the gap between execution and strategic leadership.</p>
                <p>We work with HR, Talent, and business leaders to accelerate that transition in measurable, commercially meaningful ways.</p>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="corp-cta-pipeline" className="gwa-btn-primary">
                  Discuss your leadership pipeline <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Who we work with */}
      <section className="gwa-section bg-gwa-ivory border-y border-gwa-border">
        <div className="gwa-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="gwa-eyebrow"><span className="gwa-rule" />Who we work with</p>
                <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-tight">
                  Who feels this<br />problem most.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-10">
              {PERSONAS.map((row, i) => (
                <Reveal key={row.t} delay={80 * i}>
                  <h3 className="font-serif text-2xl text-gwa-primary">{row.t}</h3>
                  <p className="mt-3 text-gwa-muted-fg leading-relaxed">{row.b}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="gwa-section bg-gwa-bg">
        <div className="gwa-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="gwa-eyebrow"><span className="gwa-rule" />Our approach</p>
                <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-tight">
                  Diagnose.<br />Design.<br />Shift.
                </h2>
                <div className="mt-7 space-y-4 text-gwa-muted-fg leading-relaxed">
                  <p>We approach leadership transition the way a strategic advisor would.</p>
                  <p>We start with the organisational reality, the politics, the stakeholder dynamics, the points where progression stalls.</p>
                  <p>Not with a generic curriculum.</p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-8 space-y-px bg-gwa-border">
              {APPROACH.map((a, i) => (
                <Reveal key={a.n} delay={80 * i} className="bg-gwa-bg p-8 lg:p-10 grid grid-cols-1 sm:grid-cols-12 gap-6 items-start">
                  <div className="sm:col-span-2">
                    <p className="font-serif text-5xl text-gwa-purple">{a.n}</p>
                  </div>
                  <div className="sm:col-span-10">
                    <h3 className="font-serif text-2xl text-gwa-primary">{a.title}</h3>
                    <p className="mt-3 text-gwa-muted-fg leading-relaxed">{a.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What we deliver */}
      <section className="gwa-section bg-gwa-secondary">
        <div className="gwa-container">
          <Reveal>
            <p className="gwa-eyebrow"><span className="gwa-rule" />What we deliver</p>
            <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-tight max-w-4xl">
              Three ways we accelerate leadership transition.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-px bg-gwa-border">
            {OFFERS.map((p, i) => (
              <Reveal key={p.slug} delay={80 * i} className="bg-gwa-secondary p-8 lg:p-10 flex flex-col">
                <p className="gwa-eyebrow">{p.eyebrow}</p>
                <h3 className="font-serif text-3xl mt-5 text-gwa-primary leading-tight">{p.title}</h3>
                <p className="mt-4 text-gwa-muted-fg leading-relaxed flex-grow">{p.body}</p>
                <p className="mt-6 text-xs uppercase tracking-[0.18em] text-gwa-primary/70">{p.format}</p>
                <Link
                  to={`/programmes#${p.slug}`}
                  data-testid={`corp-programme-${p.slug}`}
                  className="gwa-btn-ghost mt-6 self-start"
                >
                  Programme detail <ArrowRight className="w-4 h-4" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement model */}
      <section className="gwa-section bg-gwa-bg">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />Engagement model</p>
              <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-tight">
                Built around how organisations actually operate.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {ENGAGEMENT.map((line, i) => (
              <Reveal key={line} delay={60 * i}>
                <div className="flex items-start gap-4 py-4 border-b border-gwa-border">
                  <Check className="w-5 h-5 text-gwa-purple shrink-0 mt-1" />
                  <p className="text-lg text-gwa-primary leading-relaxed">{line}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="gwa-section bg-gwa-ivory border-t border-gwa-border">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />Common questions</p>
              <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-tight">
                Questions we are often asked.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={60 * i}>
                <details className="group border-b border-gwa-border py-7" data-testid={`faq-item-${i}`}>
                  <summary className="flex items-start justify-between cursor-pointer list-none gap-6">
                    <h3 className="font-serif text-xl lg:text-2xl text-gwa-primary leading-snug">{f.q}</h3>
                    <span className="font-serif text-3xl text-gwa-accent leading-none transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-5 text-gwa-muted-fg leading-relaxed max-w-3xl">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="bg-[#651A75] text-gwa-bg gwa-noise">
        <div className="gwa-container py-24 lg:py-32 relative z-10">
          <Reveal>
            <p className="gwa-eyebrow-light"><span className="gwa-rule" />Start the conversation</p>
            <h2 className="font-serif text-4xl lg:text-6xl mt-6 leading-[1.05] max-w-4xl">
              We are deliberate about who we work with. The first conversation establishes whether there is a real problem to solve, and whether we are the right partner to solve it.
            </h2>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="corp-closing-pipeline" className="gwa-btn-primary bg-gwa-bg text-gwa-primary border-gwa-bg hover:bg-gwa-gold hover:border-gwa-gold">
                Discuss your leadership pipeline <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
